import { BulkUploadFullTextPdfState, FileUploadKind } from '@/enums';
import {
  CancelBulkUploadFullTextPdfJob,
  ExportReferencesMissingFullTextPdf,
  GetBulkUploadFullTextPdfJob,
  StartBulkUploadFullTextPdfs,
} from '@/graphql';
import { apolloClient } from '@/plugins/apollo';
import { v4 as createUUID } from 'uuid';

// Terminal states of the backend BulkUploadFullTextPdfJob, distinct from the
// (UI-only) BulkUploadFullTextPdfState workflow enum above.
const JobState = Object.freeze({
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  COMPLETE: 'COMPLETE',
  CANCELLED: 'CANCELLED',
  ERROR: 'ERROR',
});
const TERMINAL_JOB_STATES = [
  JobState.COMPLETE,
  JobState.CANCELLED,
  JobState.ERROR,
];

const POLL_INTERVAL_MS = 3000;
// Stop polling after ~15 minutes so a job that never reaches a terminal state (e.g. its worker
// crashed) doesn't leave the UI spinning forever. The backend timed-out-job sweeper is the
// server-side counterpart.
const MAX_POLL_ATTEMPTS = 300;

const REFERENCE_LIST_QUERIES = [
  'GetReviewForFullTextPage',
  'GetReviewForReviewPage',
  'GetFullTextReviewPage',
  'GetReferencesByReviewIdAndFilters',
  'SearchReferences',
];

function graphQLErrorMessage(error) {
  return (
    error?.graphQLErrors?.[0]?.message ?? 'An unknown error occurred.'
  );
}

const state = {
  showingWorkflow: false,
  state: BulkUploadFullTextPdfState.AWAITING_UPLOAD,
  xmlFile: null,
  zipFile: null,
  jobId: null,
  pollIntervalId: null,
  pollAttempts: 0,
  results: null,
  error: null,
  exporting: false,
};

const actions = {
  triggerWorkflow(context) {
    context.dispatch('stopPolling');
    context.commit('setJobId', null);
    context.commit('setError', null);
    context.commit('setResults', null);
    context.commit(
      'setState',
      BulkUploadFullTextPdfState.AWAITING_UPLOAD
    );
    context.commit('setShowingWorkflow', true);
  },
  cancelWorkflow(context) {
    context.commit('setShowingWorkflow', false);
    context.dispatch('cancelUploads');
  },
  cancelUploads(context) {
    // Cancel any uploads in progress
    [context.state.xmlFile, context.state.zipFile].forEach(
      (upload) => {
        if (upload) {
          context.dispatch('upload/cancelSingleUpload', upload, {
            root: true,
          });
        }
      }
    );
    context.dispatch('stopPolling');
    const { jobId } = context.state;
    if (jobId) {
      context.commit('setJobId', null);
      apolloClient
        .mutate({
          mutation: CancelBulkUploadFullTextPdfJob,
          variables: { id: jobId },
        })
        .catch(() => {
          // Best-effort cancellation; the workflow is already closing.
        });
    }
  },
  async exportReferencesMissingFullText(context, reviewId) {
    context.commit('setExporting', true);
    try {
      const response = await apolloClient.mutate({
        mutation: ExportReferencesMissingFullTextPdf,
        variables: { reviewId },
      });
      if (response.errors) {
        throw { errors: response.errors };
      }
      const downloadInfo =
        response.data.exportReferencesMissingFullTextPdf;
      window.location.href = downloadInfo.signedDownloadUrl;
    } finally {
      context.commit('setExporting', false);
    }
  },
  async uploadFiles(context, { reviewId, xmlFile, zipFile }) {
    context.commit('setState', BulkUploadFullTextPdfState.UPLOADING);
    const promises = [
      context.dispatch('uploadXmlFile', { reviewId, xmlFile }),
      context.dispatch('uploadZipFile', { reviewId, zipFile }),
    ];
    try {
      const [
        endNoteFileUri,
        endNoteDirectoryFileUri,
      ] = await Promise.all(promises);
      const endNoteFileError =
        context.rootGetters['upload/errors'][xmlFile.id];
      const endNoteDirectoryFileError =
        context.rootGetters['upload/errors'][zipFile.id];
      if (endNoteFileError) {
        context.commit('setError', endNoteFileError);
      } else if (endNoteDirectoryFileError) {
        context.commit('setError', endNoteDirectoryFileError);
      }
      const errorOccurred =
        !!endNoteFileError || !!endNoteDirectoryFileError;
      if (errorOccurred) {
        context.commit(
          'setState',
          BulkUploadFullTextPdfState.COMPLETE
        );
        return;
      }
      await context.dispatch('startJob', {
        reviewId,
        endNoteFileUri,
        endNoteDirectoryFileUri,
      });
    } catch (error) {
      context.commit('setError', graphQLErrorMessage(error));
      context.commit('setState', BulkUploadFullTextPdfState.COMPLETE);
    }
  },
  async startJob(
    context,
    { reviewId, endNoteFileUri, endNoteDirectoryFileUri }
  ) {
    try {
      const response = await apolloClient.mutate({
        mutation: StartBulkUploadFullTextPdfs,
        variables: {
          reviewId,
          endNoteFileUri,
          endNoteDirectoryFileUri,
        },
      });
      const job = response.data.startBulkUploadFullTextPdfs;
      context.commit('setJobId', job.id);
      context.commit(
        'setState',
        BulkUploadFullTextPdfState.PROCESSING
      );
      context.dispatch('pollJob', job.id);
    } catch (error) {
      context.commit('setError', graphQLErrorMessage(error));
      context.commit('setState', BulkUploadFullTextPdfState.COMPLETE);
    }
  },
  pollJob(context, jobId) {
    context.dispatch('stopPolling');
    context.commit('setPollAttempts', 0);
    const intervalId = setInterval(() => {
      context.dispatch('checkJobStatus', jobId);
    }, POLL_INTERVAL_MS);
    context.commit('setPollIntervalId', intervalId);
  },
  async checkJobStatus(context, jobId) {
    if (context.state.pollAttempts >= MAX_POLL_ATTEMPTS) {
      context.dispatch('stopPolling');
      context.commit('setJobId', null);
      context.commit(
        'setError',
        'Processing is taking longer than expected. It may still complete in the background — reopen the review to check.'
      );
      context.commit('setState', BulkUploadFullTextPdfState.COMPLETE);
      return;
    }
    context.commit('setPollAttempts', context.state.pollAttempts + 1);
    try {
      const response = await apolloClient.query({
        query: GetBulkUploadFullTextPdfJob,
        variables: { id: jobId },
        fetchPolicy: 'network-only',
      });
      const job = response.data.bulkUploadFullTextPdfJob;
      if (TERMINAL_JOB_STATES.includes(job.state)) {
        await context.dispatch('handleJobUpdate', job);
      }
    } catch (error) {
      context.dispatch('stopPolling');
      context.commit('setJobId', null);
      context.commit('setError', graphQLErrorMessage(error));
      context.commit('setState', BulkUploadFullTextPdfState.COMPLETE);
    }
  },
  async handleJobUpdate(context, job) {
    context.dispatch('stopPolling');
    context.commit('setJobId', null);
    if (job.state === JobState.ERROR) {
      context.commit(
        'setError',
        job.error ?? 'An unknown error occurred.'
      );
    } else {
      context.commit('setResults', job);
    }
    context.commit('setState', BulkUploadFullTextPdfState.COMPLETE);
    if (job.state === JobState.COMPLETE) {
      await apolloClient.refetchQueries({
        include: REFERENCE_LIST_QUERIES,
      });
    }
  },
  stopPolling(context) {
    if (context.state.pollIntervalId) {
      clearInterval(context.state.pollIntervalId);
      context.commit('setPollIntervalId', null);
    }
  },
  uploadXmlFile(context, { reviewId, xmlFile }) {
    const upload = {
      id: createUUID(),
      reviewId,
      file: xmlFile,
      kind: FileUploadKind.REFERENCE_END_NOTE,
      contentType: 'application/xml',
    };
    context.commit('setXmlFile', upload);
    return context.dispatch('upload/uploadFile', upload, {
      root: true,
    });
  },
  uploadZipFile(context, { reviewId, zipFile }) {
    const upload = {
      id: createUUID(),
      reviewId,
      file: zipFile,
      kind: FileUploadKind.END_NOTE_DIRECTORY,
      contentType: 'application/zip',
    };
    context.commit('setZipFile', upload);
    return context.dispatch('upload/uploadFile', upload, {
      root: true,
    });
  },
};

const mutations = {
  setShowingWorkflow(state, showingWorkflow) {
    state.showingWorkflow = showingWorkflow;
  },
  setState(state, stateValue) {
    state.state = stateValue;
  },
  setXmlFile(state, file) {
    state.xmlFile = file;
  },
  setZipFile(state, file) {
    state.zipFile = file;
  },
  setJobId(state, jobId) {
    state.jobId = jobId;
  },
  setPollIntervalId(state, pollIntervalId) {
    state.pollIntervalId = pollIntervalId;
  },
  setPollAttempts(state, pollAttempts) {
    state.pollAttempts = pollAttempts;
  },
  setResults(state, results) {
    state.results = results;
  },
  setError(state, error) {
    state.error = error;
  },
  setExporting(state, exporting) {
    state.exporting = exporting;
  },
};

const getters = {
  showingWorkflow: (state) => state.showingWorkflow,
  state: (state) => state.state,
  awaitingUpload: ({ state }) =>
    state === BulkUploadFullTextPdfState.AWAITING_UPLOAD,
  uploading: ({ state }) =>
    state === BulkUploadFullTextPdfState.UPLOADING,
  processing: ({ state }) =>
    state === BulkUploadFullTextPdfState.PROCESSING,
  complete: ({ state }) =>
    state === BulkUploadFullTextPdfState.COMPLETE,
  xmlFileId: ({ xmlFile }) => xmlFile?.id,
  zipFileId: ({ zipFile }) => zipFile?.id,
  xmlFileName: ({ xmlFile }) => xmlFile?.file.name,
  zipFileName: ({ zipFile }) => zipFile?.file.name,
  xmlFileProgress: (state, getters, rootState, rootGetters) =>
    rootGetters['upload/uploadProgress'][getters.xmlFileId] * 100,
  zipFileProgress: (state, getters, rootState, rootGetters) =>
    rootGetters['upload/uploadProgress'][getters.zipFileId] * 100,
  results: (state) => state.results,
  error: (state) => state.error,
  exporting: (state) => state.exporting,
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
